<template>
  <div id="order-items" v-if="order.data_type">
    <Spin fix v-if="loading"></Spin>
    <vxe-table :data="orderItems" size="mini" :footer-method="footerTotal" show-footer border>
      <vxe-table-column type="seq" align="center" width="30"></vxe-table-column>
      <vxe-table-column :title="$t('order.product_number')" width="120">
        <template #default="{ row }">
          <span v-if="row.product && row.product.product_type === 'service'">{{ row.product.product_number }}</span>
          <span v-else-if="(row.referenced_type === 'product' ||
                            row.referenced_type === 'manufacturer_part') &&
                            row.manufacturer_part && row.manufacturer_part.part_identity">
                            {{ row.manufacturer_part.part_identity.part_number }}
          </span>
          <span class="error" v-else>{{ $t('order.missing_data_error') }}</span>
        </template>
      </vxe-table-column>
      <vxe-table-column :title="$t('order.product')" min-width="200">
        <template #default="{ row }">
          <div  v-if="row.referenced_type === 'product'">
            <div>
              <span v-if="row.product.english_name">{{ order.accounting_company === 'NexGroup Inc.' ? row.product.english_name : row.product.product_name }} </span>
              <span v-else>{{row.product.product_name}}</span>
            </div>
            <div class="error" v-if="productNameFieldValid(row)">{{ $t('order.missing_part_or_version') }}</div>
          </div>
          <div v-else-if="row.referenced_type === 'manufacturer_part'">
            <span>{{ row.referenced.name }}</span>
          </div>
          <div v-else-if="row.referenced_type === 'fee_type'">
            <span>{{ row.referenced.title }}</span>
          </div>
        </template>
      </vxe-table-column>
      <vxe-table-column field="quantity" :title="$t('order.quantity')" align="right" width="80"></vxe-table-column>
      <vxe-table-column field="unit_price" :title="$t('order.unit_price')" align="right" width="120">
        <template #default="{ row }">{{ intlCurrencyNumber(row.currency, row.unit_price, 4, 4) }}</template>
      </vxe-table-column>
      <vxe-table-column field="unit_cost" :title="$t('order.unit_cost')" align="right" width="120">
        <template #default="{ row }">{{ intlCurrencyNumber(row.currency, row.unit_cost, 4, 4) }}</template>
      </vxe-table-column>
      <vxe-table-column field="margin" :title="$t('order.margin')" align="right" width="120">
        <template #default="{ row }">{{ intlCurrencyNumber(row.currency, row.margin, 4, 4) }}</template>
      </vxe-table-column>
      <vxe-table-column field="subtotal" :title="$t('order.subtotal')" align="right" width="120">
        <template #default="{ row }">{{ intlCurrencyNumber(row.currency, row.unit_price * row.quantity, 4, 4) }}</template>
      </vxe-table-column>
      <vxe-table-column field="total_tax" v-if="order.data_type != 'order_data_type__export'" :title="$t('order.total_tax')" align="right" width="100" >
        <template #default="{ row }">{{ intlCurrencyNumber(row.currency, row.total_tax, 4, 4) }}</template>
      </vxe-table-column>
      <vxe-table-column field="delivery_date" :title="$t('order.delivery_date')" width="100">
        <template #default="{ row }">{{ row.delivery_date ? moment(row.delivery_date).format('YYYY-MM-DD') : '' }} <span v-if="!row.delivery_date" class="error">{{ $t('order.missing_data_error') }}</span></template>
      </vxe-table-column>
      <vxe-table-column width="130" align="center" :title="$t('order.actions')">
        <template #default="{ row }">
          <Button type="text" size="small" @click="handleEditClicked(row)" :disabled="disabled">{{ $t('edit') }}</Button>
          <Button type="text" size="small" @click="handleRemovalClicked(row)" :loading="itemDeleting[row.id]" :disabled="disabled">{{ $t('remove') }}</Button>
        </template>
      </vxe-table-column>
    </vxe-table>

    <h3>{{ $t('order.additional_fees') }}</h3>
    <vxe-table :data="feeItems" size="mini" border>
      <vxe-table-column type="seq" align="center" width="30"></vxe-table-column>
      <vxe-table-column field="description" :title="$t('description')"></vxe-table-column>
      <vxe-table-column field="quantity" :title="$t('quantity')" width="120" align="right"></vxe-table-column>
      <vxe-table-column field="unit_price" :title="$t('order.price')" width="160" align="right">
        <template #default="{ row }">{{ intlCurrencyNumber(order.currency, row.unit_price, 4, 4) }}</template>
      </vxe-table-column>
      <vxe-table-column :title="$t('order.subtotal')" width="120" align="right">
        <template #default="{ row }">{{ intlCurrencyNumber(row.currency, row.unit_price * row.quantity, 4, 4) }}</template>
      </vxe-table-column>
      <vxe-table-column field="currency" :title="$t('currency')" width="80" align="center"></vxe-table-column>
      <vxe-table-column :title="$t('order.fee_type')" width="320">
        <template #default="{ row }">
          <div>{{ row.referenced.title }}</div>
          <span style="color: #808695; font-size: 90%;">{{ row.referenced.description }}</span>
        </template>
      </vxe-table-column>
      <vxe-table-column width="130" align="center" :title="$t('order.actions')">
        <template #default="{ row }">
          <Button type="text" size="small" @click="handleEditClicked(row)" :disabled="disabled">{{ $t('edit') }}</Button>
          <Button type="text" size="small" @click="handleRemovalClicked(row)" :loading="itemDeleting[row.id]" :disabled="disabled">{{ $t('remove') }}</Button>
        </template>
      </vxe-table-column>
    </vxe-table>

    <div class="text-align-right" style="margin-top: 32px; font-size: 120%">
      <strong>Grand total: </strong>{{ intlCurrencyNumber(order.currency, grandTotal, 4, 4) }}
    </div>

    <div class="actions text-align-right">
      <Button icon="ios-add" type="primary" @click="handleAddItemClick" :disabled="disabled">{{ $t('order.add_item') }}</Button>
    </div>

    <Drawer :title="$t('order.order_item_form')" v-model="showOrderItemForm" width="540" :mask-closable="false">
      <OrderItemForm ref="orderItemFormRef"
                     :order-id="orderId"
                     :orderType="order.data_type"
                     :currency="currency"
                     :after-saved="refresh">
      </OrderItemForm>
    </Drawer>
  </div>
</template>

<script>
import { intlCurrencyNumber } from '@/libs/util'
import { mapActions, mapGetters } from 'vuex'
import { OrderItemForm } from './forms'
import moment from 'moment'
export default {
  name: 'OrderItems',
  components: {
    OrderItemForm
  },
  props: {
    order: Object,
    disabled: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters(['orderItemsByOrder']),
    orderItems () {
      var items = this.orderItemsByOrder(this.orderId).filter((x) => x.referenced_type !== 'fee_type')
      return items
    },
    orderId () {
      return this.order.id
    },
    currency () {
      return this.order.currency
    },
    feeItems () {
      return this.orderItemsByOrder(this.orderId).filter((x) => x.referenced_type === 'fee_type')
    },
    grandTotal () {
      return _.sumBy(this.orderItemsByOrder(this.orderId), x => x.subtotal)
    }
  },
  data () {
    return {
      loading: false,
      itemDeleting: {},
      showOrderItemForm: false
    }
  },
  mounted () {
    this.getOrderItems(this.orderId)
  },
  methods: {
    ...mapActions(['getOrderItems', 'deleteOrderItem']),
    intlCurrencyNumber,
    moment,
    footerTotal () {
      let total = _.sumBy(this.orderItems, (item) => {
        return item.unit_price * item.quantity
      })
      let margin = _.sumBy(this.orderItems, (item) => {
        return item.margin
      })
      if (this.order.data_type !== 'order_data_type__export') {
        let tax = _.sumBy(this.orderItems, 'total_tax')
        return [['', this.$t('order.total'), '', '', '', '',
          this.intlCurrencyNumber(this.currency, margin, 4, 4),
          this.intlCurrencyNumber(this.currency, total, 4, 4),
          this.intlCurrencyNumber(this.currency, tax, 4, 4)]]
      } else {
        return [['', this.$t('order.total'), '', '', '', '',
          this.intlCurrencyNumber(this.currency, margin, 4, 4),
          this.intlCurrencyNumber(this.currency, total, 4, 4)]]
      }
    },
    handleEditClicked (row) {
      let data = _.cloneDeep(row)
      this.$refs['orderItemFormRef'].setData(data)
      this.showOrderItemForm = true
    },
    handleRemovalClicked (row) {
      this.itemDeleting[row.id] = true
      this.deleteOrderItem(row.id).finally(() => { this.itemDeleting[row.id] = false })
    },
    handleAddItemClick () {
      this.$refs['orderItemFormRef'].setData({
        product: null,
        currency: this.currency,
        exchange_rate: this.order.exchange_rate,
        tax_rate: 0.13,
        price_has_tax: false
      })
      this.showOrderItemForm = true
    },
    refresh () {
      this.showOrderItemForm = false
      this.getOrderItems(this.orderId)
        .then(() => {
          this.$emit('on-order-item-updated', { grandTotal: this.grandTotal })
        })
    },
    productNameFieldValid (row) {
      let invalidNameField = true
      let product = row.product
      if (product && product.product_type === "service") {
        invalidNameField = false
      } else {
        let manufacturerParts = row.product.manufacturer_parts
        let assembledParts = manufacturerParts.length > 0
          ? manufacturerParts.filter(pmp => pmp.component_type === "assembled_part") : []
        invalidNameField = assembledParts.length === 0 ? false : !assembledParts.every(ap => ap.bom)
      }
      return invalidNameField
    }
  },
  watch: {
    orderId (newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.loading = true
        this.getOrderItems(newVal).finally(() => { this.loading = false })
      }
    }
  }
}
</script>
<style lang="less">
.vxe-footer--row {
  font-weight: bold;
  td:nth-child(2) {
    text-align: right;
  }
}
#order-items .vxe-table--body tbody tr td:nth-child(2) span {
  white-space: nowrap;
}
</style>
